import { useState, useCallback, useEffect, useMemo, type CSSProperties } from 'react'
import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { LuArrowRight, LuArrowUpRight, LuCommand, LuMaximize2, LuMinimize2, LuPickaxe, LuRadio, LuRoute, LuTruck } from 'react-icons/lu'
import TrasladosActivosView from '../Traslados/components/TrasladosActivosView'
import { shipmentsService } from '../../api/services/shipments.service'
import { proveedoresService } from '../../api/services'
import type { ShipmentListItemResponse } from '../../api/models/response'
import { accentFor, fonts, mineral, shadows } from '../../theme'
import { useT } from '../../i18n'
import { parseUtcDate } from '../../lib/dates'
import { getShipmentStateBadge } from '../../lib/shipmentStates'
import { PauseBadge } from '../../components/PauseBadge'
import { useAuth } from '../../contexts/AuthContext'
import { useMenu } from '../../auth/PermissionContext'
import { MenuIcon } from '../../auth/iconMap'
import { resolveMenuLabel } from '../../auth/resolveMenuLabel'
import { canOpen, flattenLeaves, pushRecent } from '../../components/layout/nav'
import AnimatedNumber from '../../components/brand/AnimatedNumber'

/** Accesos rápidos: los flujos que más se usan, si el rol los tiene. */
const QUICK = [
  'identity.directory.newMiner',
  'logistics.dispatch.batches',
  'plant.reception.list',
  'identity.documents.expedientes',
  'closeout.financial.settlements',
  'analytics.predictive.supplierRisk',
]

function greeting(d: Date) {
  const h = d.getHours()
  if (h < 12) return 'Buenos días'
  if (h < 19) return 'Buenas tardes'
  return 'Buenas noches'
}

/** La cadena completa en una línea: de la mina (cobre) al cierre (sabana). */
const VETA = `linear-gradient(90deg, ${mineral.cobre}, ${mineral.oro}, ${mineral.turquesa}, ${mineral.lapis}, ${mineral.rodocrosita}, ${mineral.amatista}, ${mineral.sabana})`

const rise = (i: number) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.06 * i, duration: 0.45, ease: [0.2, 0.7, 0.2, 1] as [number, number, number, number] },
})

export default function Dashboard() {
  const { t } = useT()
  const navigate = useNavigate()
  const { user } = useAuth()
  const menu = useMenu()
  const [activeCount, setActiveCount] = useState<number | null>(null)
  const handleActiveCountChange = useCallback((count: number) => setActiveCount(count), [])
  const [recentShipments, setRecentShipments] = useState<ShipmentListItemResponse[] | null>(null)
  const [shipmentsTotal, setShipmentsTotal] = useState<number | null>(null)
  const [minersTotal, setMinersTotal] = useState<number | null>(null)
  const [mapWide, setMapWide] = useState(false)

  useEffect(() => {
    shipmentsService.getAll({ page: 1, pageSize: 8 })
      .then(res => { setRecentShipments(res.items ?? []); setShipmentsTotal(res.totalCount ?? 0) })
      .catch(() => { setRecentShipments([]); setShipmentsTotal(null) })
    proveedoresService.getAll({ page: 1, pageSize: 1 })
      .then(res => setMinersTotal(res.totalCount ?? 0))
      .catch(() => setMinersTotal(null))
  }, [])

  const quick = useMemo(() => {
    const leaves = flattenLeaves(menu)
    return QUICK.map(code => leaves.find(l => l.item.code === code)).filter(l => l && canOpen(l.item))
  }, [menu])

  const now = new Date()
  const firstName = (user?.fullName ?? '').split(' ')[0] || 'equipo'

  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    const d = parseUtcDate(iso)
    return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) + ' · ' +
      d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  }

  const pulse = [
    { label: 'En ruta ahora', value: activeCount, color: mineral.oro, icon: LuTruck, live: true },
    { label: 'Traslados registrados', value: shipmentsTotal, color: mineral.turquesa, icon: LuRoute },
    { label: 'Mineros en directorio', value: minersTotal, color: mineral.rodocrosita, icon: LuPickaxe },
  ]

  return (
    <Flex direction="column" gap={7}>
      {/* Panel de inicio: saludo + pulso + accesos en una sola tarjeta compacta.
          Cada cifra y cada acceso lleva el mineral de su módulo: la gama completa
          aparece aquí de una vez, y el color dice a qué parte del sistema vas. */}
      <motion.div {...rise(0)}>
        <Box position="relative" borderRadius="22px" bg={mineral.surface} border={`1px solid ${mineral.line}`} boxShadow={shadows.md} overflow="hidden">
          {/* Veta multicolor: la cadena completa, de la mina al cierre */}
          <Box h="4px" style={{ background: VETA }} />
          <Flex px={{ base: 5, md: 7 }} pt={5} pb={4} gap={6} align={{ base: 'flex-start', lg: 'center' }} justify="space-between" direction={{ base: 'column', lg: 'row' }}>
            <Box>
              <Text fontFamily={fonts.mono} fontSize="10.5px" letterSpacing="0.16em" color={mineral.ink3} textTransform="uppercase">
                {now.toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long' })}
              </Text>
              <Text fontFamily={fonts.display} fontWeight={800} fontSize={{ base: '26px', md: '30px' }} lineHeight="1.05" letterSpacing="-0.035em" color={mineral.ink} mt={1}>
                {greeting(now)}, {firstName}
              </Text>
              <Text fontSize="14px" color={mineral.ink3} mt={1}>Lo que está en ruta, lo que entró a planta y quién lo trajo.</Text>
            </Box>

            <Flex gap={2.5} wrap="wrap">
              {pulse.map((p, i) => (
                <motion.div key={p.label} {...rise(i + 1)}>
                  <Flex align="center" gap={3} pl={3} pr={5} py={2.5} borderRadius="16px" bg={`${p.color}14`} border={`1px solid ${p.color}33`} minW="150px">
                    <Flex w="38px" h="38px" borderRadius="12px" align="center" justify="center" bg={p.color} color="#fff" position="relative" flexShrink={0}>
                      {p.live && (
                        <motion.span animate={{ scale: [1, 1.5], opacity: [0.5, 0] }} transition={{ duration: 1.8, repeat: Infinity }}
                          style={{ position: 'absolute', inset: 0, borderRadius: 12, background: p.color }} />
                      )}
                      <p.icon size={18} style={{ position: 'relative' }} />
                    </Flex>
                    <Box lineHeight="1.1">
                      <Text fontFamily={fonts.display} fontWeight={800} fontSize="26px" letterSpacing="-0.03em" color={mineral.ink}>
                        <AnimatedNumber value={p.value} />
                      </Text>
                      <Text fontSize="12px" color={mineral.ink3} whiteSpace="nowrap">{p.label}</Text>
                    </Box>
                  </Flex>
                </motion.div>
              ))}
            </Flex>
          </Flex>

          {quick.length > 0 && (
            <Grid px={{ base: 5, md: 7 }} pb={6} pt={1} gap={3}
              templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: `repeat(${quick.length + 1}, 1fr)` }}>
              {quick.map((l, i) => {
                const acc = accentFor(l!.section.code)
                return (
                  <motion.button
                    key={l!.item.id}
                    type="button"
                    {...rise(i + 2)}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => { pushRecent(l!.item.route!); navigate(l!.item.route!) }}
                    className="k-tile"
                    style={{ ['--tile' as string]: acc.color, ['--tile-deep' as string]: acc.deep, ['--tile-soft' as string]: acc.soft } as CSSProperties}
                  >
                    <span className="k-tile-fill" />
                    <span className="k-tile-mark"><MenuIcon name={l!.section.icon} size={64} /></span>
                    <span className="k-tile-icon"><MenuIcon name={l!.section.icon} size={16} /></span>
                    <span className="k-tile-title">{resolveMenuLabel(l!.item.label, t)}</span>
                    <span className="k-tile-sub">{resolveMenuLabel(l!.section.label, t, { short: true })}</span>
                    <span className="k-tile-go"><LuArrowUpRight size={16} /></span>
                  </motion.button>
                )
              })}
              <motion.button
                type="button"
                {...rise(quick.length + 2)}
                whileTap={{ scale: 0.97 }}
                onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
                className="k-tile k-tile-search"
              >
                <span className="k-tile-fill" />
                <span className="k-tile-mark"><LuCommand size={58} /></span>
                <span className="k-tile-icon"><LuCommand size={16} /></span>
                <span className="k-tile-title">Ir a cualquier pantalla</span>
                <span className="k-tile-sub">Ctrl K</span>
              </motion.button>
            </Grid>
          )}
        </Box>
      </motion.div>

      {/* Rutas en vivo + bitácora EN PARALELO. "Ampliar mapa" lleva el mapa a todo el
          ancho (y más alto) y baja la bitácora; las dos cajas se reacomodan con una
          transición de layout, no con un salto. */}
      <motion.div {...rise(4)}>
        <Grid templateColumns={{ base: '1fr', xl: mapWide ? '1fr' : 'minmax(0, 1.55fr) minmax(360px, 1fr)' }} gap={6} alignItems="start">
          <motion.div layout transition={{ type: 'spring', stiffness: 260, damping: 32 }} style={{ minWidth: 0 }}>
            <Flex justify="space-between" align="center" mb={3} gap={3}>
              <Box minW={0}>
                <Text fontFamily={fonts.display} fontWeight={800} fontSize="20px" color={mineral.ink} letterSpacing="-0.03em">{t('dashboard.title')}</Text>
                <Text fontSize="13px" color={mineral.ink3} lineClamp={1}>{t('dashboard.subtitle')}</Text>
              </Box>
              <Flex gap={2} align="center" flexShrink={0}>
                <Flex align="center" gap={2} px={3} h="32px" borderRadius="full" bg={mineral.oroSoft} color={mineral.oroDeep} fontSize="12px" fontWeight={700}>
                  <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.4, repeat: Infinity }} style={{ display: 'flex' }}>
                    <LuRadio size={14} />
                  </motion.span>
                  {activeCount ?? 0} en ruta
                </Flex>
                <Flex as="button" {...{ type: 'button' }} align="center" gap={1.5} px={3} h="32px" borderRadius="full" cursor="pointer"
                  border={`1px solid ${mineral.line}`} bg={mineral.surface} color={mineral.ink2} fontSize="12.5px" fontWeight={600}
                  _hover={{ borderColor: mineral.lineStrong, color: mineral.ink }} transition="all .15s"
                  onClick={() => setMapWide(w => !w)} aria-pressed={mapWide}>
                  {mapWide ? <LuMinimize2 size={14} /> : <LuMaximize2 size={14} />}
                  {mapWide ? 'Reducir mapa' : 'Ampliar mapa'}
                </Flex>
              </Flex>
            </Flex>
            <Box borderRadius="20px" overflow="hidden" border={`1px solid ${mineral.line}`} boxShadow={shadows.md} bg={mineral.surface}>
              <TrasladosActivosView onCountChange={handleActiveCountChange} height={mapWide ? '72vh' : '520px'} />
            </Box>
          </motion.div>

          {/* Bitácora de movimientos */}
          <motion.div layout transition={{ type: 'spring', stiffness: 260, damping: 32 }} style={{ minWidth: 0 }}>
            <Flex justify="space-between" align="center" mb={3} gap={3}>
              <Box>
                <Text fontFamily={fonts.display} fontWeight={800} fontSize="20px" color={mineral.ink} letterSpacing="-0.03em">{t('dashboard.recentActivity')}</Text>
                <Text fontSize="13px" color={mineral.ink3}>Los últimos lotes que se movieron</Text>
              </Box>
              <Flex as="button" {...{ type: 'button' }} align="center" gap={1.5} fontSize="13px" fontWeight={600} color={mineral.ocreDeep} cursor="pointer"
                _hover={{ gap: 2.5 }} transition="gap .2s" onClick={() => navigate('/app/traslados')}>
                Ver todo <LuArrowRight size={15} />
              </Flex>
            </Flex>
            <Box borderRadius="20px" border={`1px solid ${mineral.line}`} boxShadow={shadows.md} bg={mineral.surface} overflow="hidden"
              h={mapWide ? 'auto' : { base: 'auto', xl: '520px' }} overflowY="auto">
              {recentShipments === null ? (
                <Box p={5}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Box key={i} h="58px" mb={2} borderRadius="12px" bg={mineral.sunk} opacity={1 - i * 0.16} />
                  ))}
                </Box>
              ) : recentShipments.length === 0 ? (
                <Text px={6} py={10} textAlign="center" color={mineral.ink3}>No hay traslados recientes</Text>
              ) : (
                <Box>
                  {recentShipments.map((s, i) => {
                    const badge = getShipmentStateBadge(s.shipmentState)
                    return (
                      <motion.div key={s.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                        <Flex gap={3} px={5} py={3.5} align="flex-start" borderBottom={i < recentShipments.length - 1 ? `1px solid ${mineral.line}` : 'none'}
                          _hover={{ bg: mineral.surfaceAlt }} transition="background .15s" cursor="pointer" onClick={() => navigate('/app/traslados')}>
                          {/* Hito de la línea de tiempo, en el color del estado */}
                          <Flex direction="column" align="center" pt={1.5} alignSelf="stretch">
                            <Box w="10px" h="10px" borderRadius="3px" transform="rotate(45deg)" bg={badge.color} boxShadow={`0 0 0 4px ${badge.bg}`} flexShrink={0} />
                            {i < recentShipments.length - 1 && <Box w="1.5px" flex={1} mt={2} mb={-4} bg={mineral.line} />}
                          </Flex>
                          <Box flex={1} minW={0}>
                            <Flex justify="space-between" align="center" gap={2}>
                              <Text fontFamily={fonts.mono} fontSize="12.5px" fontWeight={600} color={mineral.ink} whiteSpace="nowrap">{s.batchCode}</Text>
                              <Box px={2} py="2px" borderRadius="7px" fontSize="10px" fontWeight={700} textTransform="uppercase" letterSpacing="0.04em"
                                bg={badge.bg} color={badge.color} whiteSpace="nowrap">{badge.label}</Box>
                            </Flex>
                            <Text fontSize="13.5px" color={mineral.ink} lineClamp={1} mt={0.5}>{s.supplierName || '—'}</Text>
                            <Flex gap={2} align="center" mt={0.5} fontSize="12px" color={mineral.ink3} minW={0}>
                              <Text color={mineral.oroDeep} fontWeight={600} whiteSpace="nowrap">{s.mineralTypeName || '—'}</Text>
                              <Text>·</Text>
                              <Text lineClamp={1}>→ {s.collectionCenterName || '—'}</Text>
                              <Text ml="auto" whiteSpace="nowrap">{formatDate(s.completedAt ?? s.mobileCreatedAt)}</Text>
                            </Flex>
                            {s.isPaused && <Box mt={1.5}><PauseBadge reason={s.pauseReason} /></Box>}
                          </Box>
                        </Flex>
                      </motion.div>
                    )
                  })}
                </Box>
              )}
            </Box>
          </motion.div>
        </Grid>
      </motion.div>
    </Flex>
  )
}
